import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import {
  Redirect,
  Route,
  Switch,
  matchPath,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { GetProfileQueryQueryVariables } from "types/graphql-schema";
import { GetProfileQueryQuery } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";
import { assertEdgesNonNull, getUrlUsingTypename } from "@helpers/helpers";

import ProfileCareer from "./career-tab/profile-career-tab";
import CoachingPage from "./coaching-tab/profile-coaching-tab";
import getProfileQuery from "./graphql/get-profile-query";
import ProfileOneononeSidebar from "./oneonone-tab/profile-oneonone-sidebar";
import ProfileOneonone from "./oneonone-tab/profile-oneonone-tab";
import useProfileContentTabs from "./overview-tab/components/use-profile-content-tabs";
import ProfileEmptyState from "./profile-empty-state";
import ProfilePageSidebar from "./profile-page-sidebar";
import ProfilePrivateNotes from "./profile-private-notes";
import ProfileResponsiveTabs from "./profile-responsive-tabs";
import ProfileWorkHistory from "./profile-work-history";

const ProfilePage = () => {
  const { path } = useRouteMatch();
  const link = useLink();
  const label = useLabel();
  const location = useLocation<TFLocationState>();
  const { profileId: profileIdStr } = useParams<{
    profileId: string | undefined;
  }>();
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();
  const profileId = profileIdStr ? parseInt(profileIdStr) : undefined;

  const { data, loading } = useQuery<
    GetProfileQueryQuery,
    GetProfileQueryQueryVariables
  >(getProfileQuery, {
    variables: { userId: profileId || -1 },
    skip: !profileId,
  });
  const profile = data?.user;
  const isManager = profile?.managersList.some(
    ({ id }) => id === currentUser.id,
  );

  const isAdmin = isAdminVar();
  const canViewCareerTab =
    profile &&
    (isAdmin ||
      profile.isInCurrentUserReportingTree ||
      currentUser.id === profile.id);

  const directReports = assertEdgesNonNull(currentUser.directReports);
  const currentUserIsManagerOfProfile = directReports.find(
    ({ id }) => id === profileId,
  );
  const showPrivateNotes =
    currentUser.id === profile?.id || !!currentUserIsManagerOfProfile;
  const showCoachingTab =
    !!currentUserIsManagerOfProfile &&
    waffle.flag_is_active("coaching-insights");
  const coachingManagerId: number = currentUser.id;

  const showTabs = profileId === currentUser.id || isManager;
  const contentTabs = useProfileContentTabs(profile);
  const availableContentTabs = contentTabs.filter((tab) => tab.available);

  const tabButtons: ToggleButtonGroupType<string>[] = compact([
    {
      value: "oneonone",
      title: label("oneonone", { titleCase: true }),
      active: location.pathname.includes(`/profile/${profileId}/oneonone`),
    },
    showTabs &&
      showPrivateNotes && {
        value: "my-notes",
        title: "Private Notes",
        active: location.pathname.includes(`/profile/${profileId}/my-notes`),
      },
    showTabs &&
      organization.featureFlags.workHistory && {
        value: "work-history",
        title: "Work History",
        active: location.pathname.includes(
          `/profile/${profileId}/work-history`,
        ),
      },
    showTabs &&
      showCoachingTab && {
        value: "coaching",
        title: "Insights",
        active: location.pathname.includes(`/profile/${profileId}/coaching`),
      },
    ...availableContentTabs.map((tab) => ({
      value: tab.value,
      title: tab.label,
      active: location.pathname === `/profile/${profileId}/${tab.value}`,
    })),
    showTabs &&
      canViewCareerTab && {
        value: "career",
        title: "Career",
        active: location.pathname.includes(`/profile/${profileId}/career`),
      },
  ]);

  const handleTabClick = (button: ToggleButtonGroupType<string>) => {
    // The 1-on-1 tab lands on /profile/:id/oneonone; ProfileOneonone then
    // replaces the URL with the focused meeting id.
    link.redirect(`/profile/${profileId}/${button.value}`);
  };

  const isCoachingTab = location.pathname.includes(
    `/profile/${profileId}/coaching`,
  );

  const oneononeMatch = matchPath<{ meetingId?: string }>(location.pathname, {
    path: "/profile/:profileId/oneonone/:meetingId?",
  });
  const isOneononeTab = !!oneononeMatch;
  const selectedMeetingId = oneononeMatch?.params.meetingId
    ? parseInt(oneononeMatch.params.meetingId)
    : null;

  return (
    <Layout className={isCoachingTab ? "!bg-white" : ""}>
      <Layout.Header
        className="bg-[#FCFCFD]"
        sidebarExpandedUiPreferenceKey="profileSidebarExpanded"
        title={
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {profile ? (
              <Button
                size={ButtonSize.medium}
                theme={buttonTheme.text}
                to={getUrlUsingTypename(profile)}
              >
                <Avatar user={profile} className="h-4 w-4 mr-1" />
                {profile.name}
              </Button>
            ) : (
              <span>Profile</span>
            )}
            {tabButtons.length > 0 && (
              <ProfileResponsiveTabs
                buttons={tabButtons}
                onClick={handleTabClick}
              />
            )}
          </div>
        }
      />
      <Layout.Container>
        <Layout.Main
          width="md"
          // Match the meeting-page background on the 1-on-1 tab so the sticky
          // meeting header (bg-[#FCFCFD]) blends with the surrounding page.
          className={isOneononeTab ? "bg-[#FCFCFD]" : undefined}
          sidebarExpandedUiPreferenceKey="profileSidebarExpanded"
        >
          {loading ? (
            <Loading>Loading profile...</Loading>
          ) : !profile ? (
            <div>
              <ProfileEmptyState>Profile not found.</ProfileEmptyState>
            </div>
          ) : (
            <Switch>
              <Route exact path={path}>
                <Redirect to={`/profile/${profile.id}/oneonone`} />
              </Route>
              <Route path={`${path}/oneonone/:meetingId?`}>
                <ProfileOneonone profileId={profile.id} />
              </Route>
              {showPrivateNotes && (
                <Route exact path={`${path}/my-notes`}>
                  <ProfilePrivateNotes
                    profileId={profile.id}
                    disabled={false}
                    showHeader
                  />
                </Route>
              )}
              {availableContentTabs.map((tab) => (
                <Route exact key={tab.value} path={`${path}/${tab.value}`}>
                  {tab.render()}
                </Route>
              ))}
              <Route exact path={`${path}/work-history`}>
                <ProfileWorkHistory profileId={profile.id} />
              </Route>
              {canViewCareerTab && (
                <Route exact path={`${path}/career`}>
                  <ProfileCareer profileId={profile.id} />
                </Route>
              )}
              {showCoachingTab && (
                <Route exact path={`${path}/coaching`}>
                  <CoachingPage
                    profileId={profile.id}
                    managerId={coachingManagerId}
                  />
                </Route>
              )}
            </Switch>
          )}
        </Layout.Main>
        {profile && isOneononeTab ? (
          <ProfileOneononeSidebar meetingId={selectedMeetingId} />
        ) : profile ? (
          <ProfilePageSidebar profileId={profile.id} />
        ) : (
          <Layout.Sidebar sidebarExpandedUiPreferenceKey="profileSidebarExpanded">
            <div />
          </Layout.Sidebar>
        )}
      </Layout.Container>
    </Layout>
  );
};

export default ProfilePage;
